import { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Building2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SECTION_META = {
  violations: { label: 'Violations (DOB + ECB + HPD)', Icon: AlertTriangle, accent: '#E5441A' },
  dob_filings: { label: 'DOB Filings', Icon: Building2, accent: '#1B4A76' },
};

// Colored status pill styles for violations.
const VIOL_STATUS_STYLE = {
  Active: { backgroundColor: '#FDECEA', color: '#E5441A' },
  Pending: { backgroundColor: '#FEF3C7', color: '#B45309' },
  Resolved: { backgroundColor: '#E6F4EA', color: '#137333' },
  Dismissed: { backgroundColor: '#EEF2F6', color: '#5A7A99' },
};

// Agency badge styles for the discovery rows.
const AGENCY_BADGE = {
  DOB: { backgroundColor: '#E7EEF6', color: '#1B4A76' },
  ECB: { backgroundColor: '#EDE9FE', color: '#6D28D9' },
  HPD: { backgroundColor: '#CFFAFE', color: '#0E7490' },
};

// Review + confirm modal for the BIN bulk pull. Nothing is added until the user checks
// items and clicks "Add Selected". Defaults to everything UNCHECKED.
export function BulkPullReview({ open, onOpenChange, propertyId, data, onAdded }) {
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);

  const groups = useMemo(() => ({
    violations: Array.isArray(data?.violations) ? data.violations : [],
    dob_filings: Array.isArray(data?.dob_filings) ? data.dob_filings : [],
  }), [data]);

  // Seed the checkbox state from each item's recency-based default when the modal opens.
  useEffect(() => {
    if (!open || !data) return;
    const init = {};
    [...groups.violations, ...groups.dob_filings].forEach(it => { init[it.key] = !!it.default_checked; });
    setSelected(init);
  }, [open, data, groups]);

  const total = groups.violations.length + groups.dob_filings.length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggle = (key) => setSelected(s => ({ ...s, [key]: !s[key] }));
  const toggleGroup = (items, on) => setSelected(s => {
    const next = { ...s };
    items.forEach(it => { next[it.key] = on; });
    return next;
  });

  const addSelected = async () => {
    const violations = groups.violations.filter(it => selected[it.key]);
    const dob_filings = groups.dob_filings.filter(it => selected[it.key]);
    if (violations.length + dob_filings.length === 0) return;
    setAdding(true);
    try {
      const r = await api.post(`/properties/${propertyId}/bulk-add`, { violations, dob_filings });
      const a = r.data?.added || {};
      const parts = [];
      if (a.violations) parts.push(`${a.violations} violation${a.violations === 1 ? '' : 's'}`);
      if (a.dob_filings) parts.push(`${a.dob_filings} DOB filing${a.dob_filings === 1 ? '' : 's'}`);
      toast.success(parts.length ? `Added ${parts.join(' and ')}` : 'No new records added');
      onOpenChange(false);
      setSelected({});
      onAdded && onAdded(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add records');
    }
    setAdding(false);
  };

  const renderSection = (groupKey) => {
    const items = groups[groupKey];
    if (!items.length) return null;
    const meta = SECTION_META[groupKey];
    const allOn = items.every(it => selected[it.key]);
    const closedCount = groupKey === 'violations'
      ? items.filter(it => !(['Active', 'Pending'].includes(it.status || 'Active'))).length
      : items.filter(it => !it.status_open).length;
    return (
      <div className="mb-4" data-testid={`bulk-section-${groupKey}`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <meta.Icon className="h-3.5 w-3.5" style={{ color: meta.accent }} />
            <span className="text-sm font-semibold" style={{ color: '#010A21' }}>{meta.label}</span>
            <span className="text-xs text-muted-foreground" data-testid={`bulk-count-${groupKey}`}>
              ({items.length}{closedCount ? `, ${closedCount} closed` : ''})
            </span>
          </div>
          <button
            onClick={() => toggleGroup(items, !allOn)}
            className="text-xs hover:underline"
            style={{ color: '#1B4A76' }}
            data-testid={`bulk-selectall-${groupKey}`}
          >
            {allOn ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="rounded-md overflow-hidden" style={{ border: '1px solid #E2E8F0' }}>
          {items.map((it, idx) => {
            const on = !!selected[it.key];
            const primary = groupKey === 'violations' ? it.violation_number : it.job_number;
            const penaltyStr = (groupKey === 'violations' && Number(it.penalty_amount) > 0)
              ? `$${Number(it.penalty_amount).toLocaleString()}` : null;
            const sub = groupKey === 'violations'
              ? [it.description, it.violation_category, penaltyStr, it.resolution_date ? `Resolved ${it.resolution_date}` : null].filter(Boolean).join(' · ')
              : [it.source, it.status_label, it.filing_date].filter(Boolean).join(' · ');
            const vStyle = VIOL_STATUS_STYLE[it.status] || VIOL_STATUS_STYLE.Active;
            return (
              <label
                key={it.key}
                htmlFor={`bulk-cb-${it.key}`}
                className="flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                style={{ borderTop: idx === 0 ? 'none' : '1px solid #EEF2F6', backgroundColor: on ? '#F1F6FB' : 'transparent' }}
                data-testid={`bulk-item-${it.key}`}
              >
                <Checkbox id={`bulk-cb-${it.key}`} checked={on} onCheckedChange={() => toggle(it.key)} className="mt-0.5" data-testid={`bulk-check-${it.key}`} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 min-w-0">
                    {groupKey === 'violations' && it.source && (
                      <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={AGENCY_BADGE[it.source] || AGENCY_BADGE.DOB} data-testid={`bulk-agency-${it.key}`}>{it.source}</span>
                    )}
                    <span className="block text-sm font-medium truncate" style={{ color: '#010A21', fontFamily: "'DM Mono', monospace" }}>{primary}</span>
                  </span>
                  {sub && <span className="block text-xs text-muted-foreground truncate">{sub}</span>}
                </span>
                {groupKey === 'violations' && (
                  <span className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full" style={vStyle}>{it.status || 'Active'}</span>
                )}
                {groupKey === 'dob_filings' && (
                  <span className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full" style={it.status_open ? { backgroundColor: '#E7EEF6', color: '#1B4A76' } : { backgroundColor: '#EEF2F6', color: '#5A7A99' }}>{it.status_open ? 'Open' : 'Closed'}</span>
                )}
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setSelected({}); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 bg-card border-border overflow-hidden" data-testid="bulk-review-modal">
        <DialogHeader className="px-5 py-3.5 border-b border-border space-y-0.5" style={{ backgroundColor: '#132C4A' }}>
          <DialogTitle className="text-sm text-white flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Records found on NYC Open Data
          </DialogTitle>
          <DialogDescription className="text-xs" style={{ color: '#A9C2DB' }}>
            {total} record{total === 1 ? '' : 's'} matched BIN {data?.bin || ''}
            {typeof data?.open_total === 'number' ? ` · ${data.open_total} open, ${data.closed_total || 0} closed` : ''}. Select which to add. Nothing is imported until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 max-h-[60vh] overflow-y-auto" data-testid="bulk-review-body">
          {total === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No new records found for this BIN.</p>
          ) : (
            <>
              {renderSection('violations')}
              {renderSection('dob_filings')}
            </>
          )}
          {data?.notes?.dot_permits && (
            <div className="flex items-start gap-2 mt-1 rounded-md px-3 py-2" style={{ backgroundColor: '#F5F8FB', border: '1px solid #E2E8F0' }} data-testid="bulk-dot-note">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: '#5A7A99' }} />
              <p className="text-xs" style={{ color: '#5A7A99' }}>{data.notes.dot_permits}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground" data-testid="bulk-selected-count">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel-btn">Cancel</Button>
            <Button
              onClick={addSelected}
              disabled={adding || selectedCount === 0}
              className="bg-primary text-primary-foreground"
              data-testid="bulk-add-selected-btn"
            >
              {adding ? 'Adding…' : `Add Selected${selectedCount ? ` (${selectedCount})` : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BulkPullReview;
